import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import Api from '@/config/api.helper'
import { push } from 'notivue'
import { OnboardingModulo } from '@/types/plugins/session'

interface Store {
  onboardingList: OnboardingModulo[]
  getOnboardingByModulo(moduloCodigo: string): OnboardingModulo | undefined
  isOnboardingCompleted(moduloCodigo: string): boolean
  markOnboardingAsCompleted(onboardingId: number): Promise<boolean>
  skipOnboarding(onboardingId: number): Promise<boolean>
  setOnboardingList(list: OnboardingModulo[]): void
  clearOnboarding(): void
}

export const useOnboardingStore = defineStore('onboarding', (): Store => {
  const onboardingList = ref<OnboardingModulo[]>([])
  const completingRequests = ref<Set<number>>(new Set())
  const skippingRequests = ref<Set<number>>(new Set())

  function setOnboardingList(list: OnboardingModulo[]) {
    onboardingList.value = list
  }

  function clearOnboarding() {
    onboardingList.value = []
  }

  function getOnboardingByModulo(
    moduloCodigo: string
  ): OnboardingModulo | undefined {
    return onboardingList.value.find(
      (onboarding) => onboarding.modulo.codigo === moduloCodigo
    )
  }

  function isOnboardingCompleted(moduloCodigo: string): boolean {
    const onboarding = getOnboardingByModulo(moduloCodigo)
    return onboarding?.completado ?? true // Retorna true se não encontrar (não deve mostrar)
  }

  async function markOnboardingAsCompleted(
    onboardingId: number
  ): Promise<boolean> {
    if (completingRequests.value.has(onboardingId)) {
      return false
    }

    completingRequests.value.add(onboardingId)

    try {
      await Api.post(`v2/onboarding/${onboardingId}/completar`)

      // Atualiza localmente
      const onboarding = onboardingList.value.find((o) => o.id === onboardingId)
      if (onboarding) {
        onboarding.completado = true
      }

      return true
    } catch (error) {
      console.error('Erro ao marcar onboarding como completado:', error)
      push.error({ title: 'Erro ao salvar progresso do tour' })
      return false
    } finally {
      completingRequests.value.delete(onboardingId)
    }
  }

  async function skipOnboarding(onboardingId: number): Promise<boolean> {
    if (skippingRequests.value.has(onboardingId)) {
      return false
    }

    skippingRequests.value.add(onboardingId)

    try {
      await Api.post(`v2/onboarding/${onboardingId}/pular`)

      // Atualiza localmente
      const onboarding = onboardingList.value.find((o) => o.id === onboardingId)
      if (onboarding) {
        onboarding.completado = true
      }

      return true
    } catch (error) {
      console.error('Erro ao pular onboarding:', error)
      push.error({ title: 'Erro ao pular tour' })
      return false
    } finally {
      skippingRequests.value.delete(onboardingId)
    }
  }

  return {
    onboardingList: computed(() => onboardingList.value),
    getOnboardingByModulo,
    isOnboardingCompleted,
    markOnboardingAsCompleted,
    skipOnboarding,
    setOnboardingList,
    clearOnboarding,
  }
})
